/**
 * Mentor Availability service — Firestore CRUD for `mentorAvailability`.
 *
 * A document defines one recurring weekly working-hours window:
 *   {
 *     mentorId, mentorEmail,        // owner (email denormalized for rules)
 *     dayOfWeek: 0-6,               // 0 = Sunday … 6 = Saturday
 *     startMinutes, endMinutes,     // minutes from midnight, wall-clock in `timezone`
 *     timezone,                     // IANA-labelled tz, e.g. "Asia/Kolkata (IST)"
 *     active: boolean,
 *     createdAt, updatedAt
 *   }
 *
 * The instant a window represents on any given calendar date is resolved through
 * the timezone-aware helpers (windowInstantsOn), so it stays correct across DST.
 */
import firestoreService, { where } from './firestore.service';

const COLLECTION = 'mentorAvailability';

function sanitize(input = {}) {
  return {
    mentorId: input.mentorId || '',
    mentorEmail: (input.mentorEmail || '').trim().toLowerCase(),
    dayOfWeek: Number(input.dayOfWeek),
    startMinutes: Number(input.startMinutes),
    endMinutes: Number(input.endMinutes),
    timezone: input.timezone || '',
    active: input.active !== false,
  };
}

/** Sort by weekday then start time so lists/calendars read top-to-bottom. */
function sortWindows(rows) {
  return [...rows].sort(
    (a, b) => a.dayOfWeek - b.dayOfWeek || a.startMinutes - b.startMinutes
  );
}

export const mentorAvailabilityService = {
  /** Realtime subscription to one mentor's availability windows. */
  subscribeByMentor(mentorId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('mentorId', '==', mentorId)],
      (rows) => onData(sortWindows(rows)),
      onError
    );
  },

  async listByMentor(mentorId) {
    const rows = await firestoreService.getCollection(COLLECTION, [where('mentorId', '==', mentorId)]);
    return sortWindows(rows);
  },

  create(data) {
    return firestoreService.addDocument(COLLECTION, sanitize(data));
  },

  update(id, data) {
    return firestoreService.updateDocument(COLLECTION, id, sanitize(data));
  },

  remove(id) {
    return firestoreService.deleteDocument(COLLECTION, id);
  },
};

export default mentorAvailabilityService;
